import { STARTER_LOADOUT } from '@vm/content';
import type { CatalogResponse, InventoryItem, MeResponse } from '@vm/shared';
import { readJson, readLocal, writeLocal } from '../api/http';

/**
 * Offline fallback: the last profile/catalog seen online are cached so practice, Boot Camp and
 * the sandbox keep working (with the player's real loadout and look) when the API is unreachable.
 */
const ME_CACHE = 'vm.meCache';
const CATALOG_CACHE = 'vm.catalogCache';
const OFFLINE_ME = 'vm.offlineMe';

export function cacheMe(me: MeResponse): void {
  if (me.id === 'offline') writeLocal(OFFLINE_ME, JSON.stringify(me));
  else writeLocal(ME_CACHE, JSON.stringify(me));
}

export function cachedMe(): MeResponse | null {
  return readJson<MeResponse>(ME_CACHE);
}

export function cacheCatalog(c: CatalogResponse): void {
  writeLocal(CATALOG_CACHE, JSON.stringify(c));
}

export function cachedCatalog(): CatalogResponse | null {
  return readJson<CatalogResponse>(CATALOG_CACHE);
}

export function clearProfileCaches(): void {
  writeLocal(ME_CACHE, null);
  writeLocal(OFFLINE_ME, null);
}

const STARTER_ITEMS: InventoryItem[] = [
  ...STARTER_LOADOUT.weapons.filter(Boolean).map((id) => ({ type: 'weapon' as const, id, acquiredAt: '' })),
  { type: 'gadget', id: STARTER_LOADOUT.gadget, acquiredAt: '' },
  { type: 'pet', id: STARTER_LOADOUT.pet, acquiredAt: '' },
  { type: 'skin', id: 'marine', acquiredAt: '' },
  { type: 'skin', id: 'rookie', acquiredAt: '' },
  { type: 'armor', id: 'standard', acquiredAt: '' },
  { type: 'camo', id: 'none', acquiredAt: '' },
  { type: 'deathfx', id: 'shatter', acquiredAt: '' },
  { type: 'banner', id: 'recruit', acquiredAt: '' },
];

/** Local profiles saved before pets existed: add the starter pet (owned and, without a `pet` key, equipped). */
function withStarterPet(m: MeResponse): MeResponse {
  const hasPet = m.inventory.some((i) => i.type === 'pet' && i.id === STARTER_LOADOUT.pet);
  if (hasPet && m.loadout.pet !== undefined) return m;
  return {
    ...m,
    inventory: hasPet ? m.inventory : [...m.inventory, { type: 'pet', id: STARTER_LOADOUT.pet, acquiredAt: '' }],
    loadout: m.loadout.pet === undefined ? { ...m.loadout, pet: STARTER_LOADOUT.pet } : m.loadout,
  };
}

/** A local-only profile used when the player has never reached the API. */
export function offlineProfile(name: string, restrictedAcct: boolean): MeResponse {
  const saved = readJson<MeResponse>(OFFLINE_ME);
  if (saved) return withStarterPet({ ...saved, name: name || saved.name, restricted: restrictedAcct });
  return {
    id: 'offline',
    name,
    code: '',
    email: null,
    isGuest: true,
    restricted: restrictedAcct,
    role: 'player',
    createdAt: new Date().toISOString(),
    wallet: { coins: 0, gems: 0 },
    progression: { level: 1, xp: 0, xpToNext: 1000, xpInLevel: 0, prestige: 0 },
    stats: {
      kills: 0, deaths: 0, assists: 0, wins: 0, losses: 0, matches: 0, headshots: 0, voxelsDestroyed: 0, voxelsBuilt: 0,
      collapseKills: 0, playtimeSec: 0, bestStreak: 0, rating: 1000, weaponKills: {},
    },
    inventory: STARTER_ITEMS,
    loadout: { weapons: [...STARTER_LOADOUT.weapons], gadget: STARTER_LOADOUT.gadget, pet: STARTER_LOADOUT.pet },
    cosmetics: { skin: 'preset:marine', armor: 'standard', camos: {}, deathFx: 'shatter', banner: 'recruit' },
    customSkins: [],
    clan: null,
    renamed: true,
    unreadInbox: 0,
    dailyLogin: { day: 1, claimedToday: true, nextReward: {} },
    mutedUntil: null,
    tutorialDone: readLocal('vm.tutorialDone') === '1',
  };
}

// Age gate answer (kept on the device so the gate is not re-asked, and so a lost session can be
// re-created without asking again).
export function storedBirthYear(): number | null {
  const v = Number(readLocal('vm.birthYear'));
  return Number.isFinite(v) && v > 1900 ? v : null;
}

export function storeBirthYear(year: number | null): void {
  writeLocal('vm.birthYear', year === null ? null : String(year));
}

export function isUnder13(year: number): boolean {
  const now = new Date();
  // Without a birth month, assume the birthday has not happened yet this year (stricter).
  return now.getFullYear() - year - 1 < 13;
}

export function localName(): string | null {
  return readLocal('vm.localName');
}

export function storeLocalName(name: string): void {
  writeLocal('vm.localName', name);
}

export function onboardedFlag(): boolean {
  return readLocal('vm.onboarded') === '1';
}

export function setOnboardedFlag(v: boolean): void {
  writeLocal('vm.onboarded', v ? '1' : null);
}
